/**
 * Reusable CSV-download helper.
 *
 *  - Routes through the shared `api` axios client so cookies + Bearer token are
 *    attached using the same logic as the rest of the app (no chance of token
 *    key drift or stale-storage bugs).
 *  - Localizes ISO timestamps in the file to the user's local timezone.
 *  - Forces a browser download with the filename from the response or a fallback.
 *  - Shows a success toast like "CSV exported successfully – shipments-20260209.csv".
 *
 *  Returns a promise resolving to {filename, count} on success.
 */
import { api, formatApiError } from "./api";
import { toast } from "sonner";

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

function _localize(value) {
  if (typeof value !== "string" || !ISO_RE.test(value)) return value;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return value;
  }
}

function _localizeCsv(text) {
  // Parse line-by-line and localize any cell that looks like an ISO timestamp.
  // Avoids a heavy CSV parser — works correctly for our backend export which never
  // produces multi-line cells (no embedded newlines in MR/Project_Station/etc).
  const lines = text.split(/\r?\n/);
  if (lines.length === 0) return text;
  const out = [lines[0]]; // header line untouched
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) {
      out.push(line);
      continue;
    }
    const cells = [];
    let buf = "";
    let inQ = false;
    for (let c = 0; c < line.length; c++) {
      const ch = line[c];
      if (ch === '"') inQ = !inQ;
      if (ch === "," && !inQ) {
        cells.push(buf);
        buf = "";
      } else {
        buf += ch;
      }
    }
    cells.push(buf);
    const localized = cells.map((cell) => {
      const trimmed = cell.startsWith('"') && cell.endsWith('"') ? cell.slice(1, -1) : cell;
      const next = _localize(trimmed);
      return next === trimmed ? cell : next;
    });
    out.push(localized.join(","));
  }
  return out.join("\n");
}

function _filenameFromResponse(resp, fallback) {
  // Prefer X-Export-Filename (set by our backend, simpler than parsing CD),
  // fall back to Content-Disposition, then to the caller-provided fallback.
  // Axios lower-cases header names.
  const headers = resp.headers || {};
  const xname = headers["x-export-filename"] || headers["X-Export-Filename"];
  if (xname) return xname;
  const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  if (m) return m[1];
  return fallback;
}

async function _blobToText(blob) {
  if (typeof blob.text === "function") return blob.text();
  // Safari < 14 fallback
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

export async function downloadCsv({ url, params = {}, fallbackFilename = "export.csv" }) {
  let resp;
  try {
    resp = await api.get(url, {
      params,
      responseType: "blob",
      // Some Accept-anything override so axios doesn't try to parse blobs as JSON
      headers: { Accept: "text/csv,*/*" },
    });
  } catch (e) {
    // Surface the real failure to the user instead of silently dropping it.
    const status = e?.response?.status;
    let detail = "";
    try {
      if (e?.response?.data && typeof e.response.data.text === "function") {
        detail = await e.response.data.text();
      }
    } catch (_) {
      /* ignore */
    }
    if (status === 401 || status === 403) {
      toast.error("Your session expired — please sign in again to export.");
    } else if (status) {
      toast.error(`Export failed — ${status} ${formatApiError(detail) || ""}`.trim());
    } else {
      toast.error("Network error — could not export CSV");
    }
    console.error("CSV export failed", { url, status, detail, error: e });
    throw e;
  }

  const filename = _filenameFromResponse(resp, fallbackFilename);
  const xCount = parseInt(resp.headers?.["x-export-count"] || "0", 10) || 0;
  const text = await _blobToText(resp.data);
  const localized = _localizeCsv(text);
  const blob = new Blob([localized], { type: "text/csv;charset=utf-8;" });
  const dlUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = dlUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the browser has had a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(dlUrl), 4000);
  toast.success(`CSV exported – ${filename}`);
  return { filename, count: xCount };
}
